// PDFDocument.js
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";

// Register fonts for better text rendering
Font.register({
  family: "Times",
  fonts: [
    { src: "https://fonts.gstatic.com/s/timesnewroman/v1/times-new-roman.ttf" },
    {
      src: "https://fonts.gstatic.com/s/timesnewroman/v1/times-new-roman-bold.ttf",
      fontWeight: "bold",
    },
    {
      src: "https://fonts.gstatic.com/s/timesnewroman/v1/times-new-roman-italic.ttf",
      fontStyle: "italic",
    },
  ],
});

// Styles for PDF
const styles = StyleSheet.create({
  page: {
    fontFamily: "Times",
    fontSize: 11,
    paddingTop: 10,
    paddingLeft: 8,
    paddingRight: 8,
    paddingBottom: 10,
    lineHeight: 1.4,
  },
  examHeader: {
    textAlign: "center",
    borderBottom: "2pt solid #000000",
    paddingBottom: 6,
    marginBottom: 8,
  },
  examTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 4,
  },
  examSubtitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 3,
  },
  examSubject: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  examDetails: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "1.5pt solid #000000",
    borderBottom: "1.5pt solid #000000",
    paddingVertical: 3,
    marginBottom: 4,
    fontSize: 11,
    fontWeight: "bold",
  },
  examInstructions: {
    textAlign: "left",
    fontSize: 10,
    marginTop: 4,
    lineHeight: 1.3,
  },
  pageHeader: {
    textAlign: "right",
    fontSize: 9,
    color: "#666666",
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 3,
    paddingBottom: 2,
    borderBottom: "1pt solid #cccccc",
  },
  questionContainer: {
    flexDirection: "row",
    marginVertical: 3,
    alignItems: "flex-start",
  },
  questionNumber: {
    fontWeight: "bold",
    marginRight: 4,
    minWidth: 10,
    fontSize: 11,
  },
  questionContent: {
    flex: 1,
    fontSize: 11,
    lineHeight: 1.4,
  },
  questionText: {
    marginBottom: 3,
  },
  optionsContainer: {
    marginLeft: 6,
    marginVertical: 3,
  },
  optionItem: {
    flexDirection: "row",
    marginVertical: 2,
    alignItems: "flex-start",
  },
  optionLabel: {
    fontWeight: "bold",
    marginRight: 4,
    minWidth: 8,
  },
  optionText: {
    flex: 1,
  },
  questionMarks: {
    textAlign: "right",
    fontSize: 9,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 3,
  },
  mathImage: {
    maxWidth: "100%",
    marginVertical: 2,
  },
});

// Component to render math equations as images
const MathEquation = ({ equation, mathImages }) => {
  const imageData = mathImages[equation.id];
  if (!imageData) {
    return <Text>{equation.latex}</Text>;
  }

  return <Image style={styles.mathImage} src={imageData} />;
};

// Component to render rich content with math support
const RichContent = ({ content, mathImages }) => {
  if (!content) return null;

  // Handle different content types
  if (typeof content === "string") {
    return <Text>{content}</Text>;
  }

  if (content.type === "rich-text") {
    let text = content.plainText || content.html?.replace(/<[^>]*>/g, "") || "";

    // Handle equation placeholders
    if (content.equations) {
      const elements = [];
      let lastIndex = 0;

      // Find all equation placeholders
      const equationPattern = /\[EQUATION:([^\]]+)\]/g;
      let match;

      while ((match = equationPattern.exec(text)) !== null) {
        // Add text before equation
        if (match.index > lastIndex) {
          const beforeText = text.slice(lastIndex, match.index);
          if (beforeText.trim()) {
            elements.push(<Text key={`text-${lastIndex}`}>{beforeText}</Text>);
          }
        }

        // Add equation
        const equationId = match[1];
        const equation = content.equations[equationId];
        if (equation) {
          elements.push(
            <MathEquation
              key={`eq-${equationId}`}
              equation={equation}
              mathImages={mathImages}
            />
          );
        }

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        const remainingText = text.slice(lastIndex);
        if (remainingText.trim()) {
          elements.push(<Text key={`text-${lastIndex}`}>{remainingText}</Text>);
        }
      }

      return <View>{elements}</View>;
    }

    return <Text>{text}</Text>;
  }

  return <Text>{content.content || ""}</Text>;
};

// Main PDF Document component
const PDFDocument = ({ examData, organizedContent, mathImages }) => {
  return (
    <Document>
      {organizedContent.pages.map((page, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {/* Exam header on first page */}
          {page.isFirstPage && (
            <View style={styles.examHeader}>
              <Text style={styles.examTitle}>{examData.metadata.heading}</Text>
              <Text style={styles.examSubtitle}>
                {examData.metadata.subheading}
              </Text>
              <Text style={styles.examSubject}>
                {examData.metadata.subjectName}
              </Text>
              <View style={styles.examDetails}>
                <Text>Time Allowed: {examData.metadata.timeAllowed}</Text>
                <Text>Maximum Marks: {organizedContent.totalMarks}</Text>
              </View>
              <View style={styles.examInstructions}>
                <Text>
                  <Text style={{ fontWeight: "bold" }}>Instructions: </Text>
                  {examData.metadata.instructions}
                </Text>
              </View>
            </View>
          )}

          {/* Page header */}
          <Text style={styles.pageHeader}>
            Page {page.pageNumber} of {organizedContent.totalPages}
          </Text>

          {/* Sections */}
          {page.sections.map((section, sectionIndex) => (
            <View key={section.id} style={{ marginBottom: 6 }}>
              {/* Section title */}
              <Text style={styles.sectionTitle}>
                {section.title}
                {section.isContinuation ? " (continued)" : ""}
              </Text>

              {/* Questions */}
              {section.questions.map((question, questionIndex) => (
                <View key={question.id} style={styles.questionContainer}>
                  <Text style={styles.questionNumber}>
                    {question.globalIndex + 1}.
                  </Text>
                  <View style={styles.questionContent}>
                    {/* Question text */}
                    <View style={styles.questionText}>
                      <RichContent
                        content={question.richContent}
                        mathImages={mathImages}
                      />
                    </View>

                    {/* Options */}
                    {question.type === "with-options" &&
                      question.options?.length > 0 && (
                        <View style={styles.optionsContainer}>
                          {question.options.map((option, optionIndex) => (
                            <View key={option.id} style={styles.optionItem}>
                              <Text style={styles.optionLabel}>
                                ({String.fromCharCode(97 + optionIndex)})
                              </Text>
                              <View style={styles.optionText}>
                                <RichContent
                                  content={option.richContent}
                                  mathImages={mathImages}
                                />
                              </View>
                            </View>
                          ))}
                        </View>
                      )}

                    {/* Marks */}
                    <Text style={styles.questionMarks}>
                      [{question.marks || 1} mark
                      {(question.marks || 1) !== 1 ? "s" : ""}]
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          ))}
        </Page>
      ))}
    </Document>
  );
};

export default PDFDocument;
